import Cookies from 'universal-cookie';
import Axios from '@services/axios';
import { onTransformedHistory } from '../sections/history/utils';
import { saveAs } from 'file-saver';
import { getDownloadTemplateEndpoint, getUploadDateHandler, getUploadEndpoint } from './utils';
import moment from 'moment';

export const onDownloadTemplate = async ({ templateType, onChangeDownloadNotification }) => {
    const cookies = new Cookies();
    const userToken = cookies.get('userToken');

    const endpoint = getDownloadTemplateEndpoint({ templateType });

    const filename = `${templateType}.xlsx`;

    onChangeDownloadNotification({
        status: 'download',
        title: 'Downloading',
        description: `Harap tunggu, sedang mendownload file "${filename}"`,
    });

    try {
        const { data } = await Axios({
            url: endpoint,
            method: 'GET',
            responseType: 'blob',
            headers: { Authorization: 'Bearer ' + userToken },
        });

        const blob = new Blob([data], { type: 'text/xlsx' });
        saveAs(blob, `${filename}`);

        onChangeDownloadNotification({
            status: 'success',
            title: 'Success',
            description: `Download "${filename}" berhasil`,
        });
    } catch (err) {
        console.log(err);
        onChangeDownloadNotification({
            status: 'error',
            title: 'Failed',
            description: `Download file template gagal`,
        });
    }
};

export const onUploadFile = async ({
    popUpStore,
    onChangePopUpStore,
    onResetPopUpStore,
    onClose,
    onChangePopUpNotification,
    onRefetchUploadData,
    isCanUpload,
    userInfo,
    onResetFile,
    onNavigate,
    onChangePopupAssetStore,
    isConversion = false,
    onChangeSnackbarNotification,
    onSmartUploadSuccess = () => {},
    rawFilename,
}) => {
    if (!isCanUpload) return;

    const cookies = new Cookies();
    const userToken = cookies.get('userToken');
    let isErrorSendAutoFormat = false;

    const { selectedTimeUploadType, selectedTimeUploadValue, selectedFile, selectedFormatUploadType } = popUpStore;

    onChangePopUpStore({ isUploading: true });

    const start_date = getUploadDateHandler({
        type: selectedTimeUploadType,
        value: selectedTimeUploadValue,
        category: 'start_date',
    });
    const end_date = getUploadDateHandler({
        type: selectedTimeUploadType,
        value: selectedTimeUploadValue,
        category: 'end_date',
    });
    try {
        const url =
            getUploadEndpoint({ type: selectedFormatUploadType }) +
            (isConversion
                ? `?${new URLSearchParams({ smart_upload_filename: rawFilename || selectedFile.name })}`
                : '');

        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('status', selectedTimeUploadType);
        if (selectedTimeUploadType !== 'invoice') {
            formData.append('start_date', start_date);
            formData.append('end_date', end_date);
        }

        const controller = new AbortController();
        onChangePopupAssetStore({ controller });

        const { data } = await Axios({
            url: `/api/v1/${url}`,
            method: 'POST',
            headers: { Authorization: 'Bearer ' + userToken },
            data: formData,
            signal: controller.signal,
        });

        //====================== SEND TRANSLATE ====================

        if (selectedFormatUploadType === 'Auto Format') {
            try {
                await Axios({
                    url: `/api/v1/sellout/translate-send-sellout`,
                    method: 'POST',
                    headers: { Authorization: 'Bearer ' + userToken },
                    data: {
                        payload: data?.payload,
                    },
                });
            } catch (error) {
                isErrorSendAutoFormat = true;

                throw error;
            }
        }

        if (selectedFormatUploadType === 'Smart Upload' && data && data?.status === 200) {
            const mappedData = {
                sellout_summary: data?.data?.sellout_summary,
                data: {
                    success_data:
                        data?.data?.success_data?.map((item) => ({
                            id: crypto.randomUUID(),
                            ...item,
                        })) || [],
                    failed_data:
                        data?.data?.failed_data?.map((item) => ({
                            id: crypto.randomUUID(),
                            ...item,
                        })) || [],
                },
                detail_error: data?.data?.detail_error,
            };
            onSmartUploadSuccess(mappedData);
            onChangePopupAssetStore({ progress: 100 });
            await new Promise((res) => setTimeout(res, 300));
            return onNavigate('/upload-sellout/konversi');
        }

        if (isConversion && (!data?.errors || data.errors.length === 0)) {
            onNavigate('/upload-sellout');
            return onChangeSnackbarNotification({
                status: 'success',
                title: 'Sukses Upload',
                description: (
                    <>
                        Data sell out <b>{selectedFile.name}</b> berhasil diupload
                    </>
                ),
                duration: 3,
            });
        }
    } catch (err) {
        let errorMessages =
            err?.response?.data?.errors ||
            err?.response?.data?.data?.detail_error ||
            err?.response?.data?.message ||
            'Terjadi kesalahan saat mengupload file, silahkan coba lagi';
        const errorStatus = err?.response?.status;

        if (errorStatus === 500 && selectedFormatUploadType === 'Auto Format' && isErrorSendAutoFormat) {
            errorMessages = 'file yang anda upload belum dapat di auto format oleh server';
        }

        if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
            errorMessages = 'Upload file dibatalkan oleh user.';
        }

        onChangePopUpNotification({
            errorMessages,
            status: 'failed',
            additionalInfo: {
                uploadedFile: selectedFile.name,
                filePath: errorMessages?.[0]?.url_file,
                selectedFile,
                email: userInfo?.email,
                typeUpload: `${selectedTimeUploadType} - ${selectedFormatUploadType}`,
                storeName: userInfo?.nama_outlet,
                dateUpload: `${moment().format('DD MMMM YYYY - HH:mm:ss')} WIB`,
                month: moment(start_date).format('MMMM YYYY'),
                errorStatus,
            },
        });

        onResetPopUpStore();
    } finally {
        onChangePopUpStore({ isUploading: false });
        onClose();
        onRefetchUploadData();
        onResetFile();
    }
};

// ================== FETCH BRANDS ====================

export const fetchBrands = async () => {
    const cookies = new Cookies();
    const userToken = cookies.get('userToken');

    try {
        const { data } = await Axios({
            url: `/api/v1/brand/list`,
            method: 'GET',
            headers: { Authorization: 'Bearer ' + userToken },
        });

        return data?.data;
    } catch (error) {
        console.log(error);

        return [];
    }
};

export const fetchUploadTypes = async () => {
    const cookies = new Cookies();
    const userToken = cookies.get('userToken');

    try {
        const { data } = await Axios({
            url: `/api/v1/declaration/type`,
            method: 'GET',
            headers: { Authorization: 'Bearer ' + userToken },
        });

        return data?.data;
    } catch (error) {
        console.log(error);

        return [];
    }
};

export const fetchTimeUpload = async () => {
    const cookies = new Cookies();
    const userToken = cookies.get('userToken');

    try {
        const { data } = await Axios({
            url: `/api/v1/declaration/time`,
            method: 'GET',
            headers: { Authorization: 'Bearer ' + userToken },
        });

        return data?.data;
    } catch (error) {
        console.log(error);

        return [];
    }
};

export const fetchBlockedUpload = async () => {
    const cookies = new Cookies();
    const userToken = cookies.get('userToken');

    try {
        const { data } = await Axios({
            url: `/api/v1/sellout/blocked-period`,
            method: 'GET',
            headers: { Authorization: 'Bearer ' + userToken },
        });

        return data;
    } catch (error) {
        console.log(error);

        return [];
    }
};

//================================ FETCH ASSETS ===================

export const fetchAssets = async ({ onChangePopupAssetStore }) => {
    const [brandOptions, uploadTypeOptions, timeUploadOptions, blockedUpload] = await Promise.all([
        fetchBrands(),
        fetchUploadTypes(),
        fetchTimeUpload(),
        fetchBlockedUpload(),
    ]);
    onChangePopupAssetStore({
        brandOptions,
        timeUploadOptions,
        uploadTypeOptions,
        blockedUpload,
        isLoadingAsset: false,
    });
};

//================================ FETCH HISTORY AUDIT TRAIL ===================
export const fetchHistory = async ({ onChangeHistoryStore }) => {
    const cookies = new Cookies();
    const userToken = cookies.get('userToken');

    onChangeHistoryStore({ isLoadingHistoryTable: true });

    try {
        const { data } = await Axios({
            method: 'POST',
            url: `/api/v1/sellout/upload-history`,
            headers: { Authorization: 'Bearer ' + userToken },
        });
        const histories = data?.data;

        const transformedHistory = onTransformedHistory({ histories });

        onChangeHistoryStore({
            history: transformedHistory,
            isLoadingHistoryTable: false,
        });
    } catch (error) {
        console.log(error);
        onChangeHistoryStore({ history: [], isLoadingHistoryTable: false });
    }
};

//================================ FETCH ERROR UPLOAD ===================

export const fetchError = async ({ onChangeErrorStore }) => {
    const cookies = new Cookies();
    const userToken = cookies.get('userToken');

    onChangeErrorStore({ isLoadingErrorTable: true });

    try {
        const fetchedError = await Axios({
            method: 'GET',
            url: `/api/v1/sellout/failed-row`,
            headers: { Authorization: 'Bearer ' + userToken },
        });
        const errors = fetchedError?.data?.data;

        const transformedError = errors.map((error, index) => ({
            ...error,
            no: index + 1,
        }));

        onChangeErrorStore({ errors: transformedError, isLoadingErrorTable: false });
    } catch (error) {
        console.log(error);
        onChangeErrorStore({ errors: [], isLoadingErrorTable: false });
    }
};

export const onDownloadFileError = async ({ path, onChangeDownloadNotification }) => {
    const cookies = new Cookies();
    const userToken = cookies.get('userToken');

    onChangeDownloadNotification({
        status: 'download',
        title: 'Downloading',
        description: `Harap tunggu, sedang mendownload file template`,
    });

    try {
        const { data } = await Axios({
            method: 'POST',
            url: '/api/download-file',
            data: { path },
            responseType: 'blob',
            headers: { Authorization: `Bearer ${userToken}` },
        });
        // eslint-disable-next-line
        const [_, filename] = path.split('/');

        const blob = new Blob([data], { type: 'text/xlsx' });
        saveAs(blob, `${filename}.xlsx`);

        onChangeDownloadNotification({
            status: 'success',
            title: 'Success',
            description: `Download "${filename}" berhasil`,
        });
    } catch (error) {
        console.log(error);

        onChangeDownloadNotification({
            status: 'error',
            title: 'Failed',
            description: `Download file gagal`,
        });
    }
};
